import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, ArrowRight, Sparkles, MessageSquare, LayoutDashboard,
  FileText, BarChart3, Settings, Users, Key, Wrench, Receipt,
  Zap, Hash, Clock, Cpu,
} from 'lucide-react';
import { modalCenter } from '../../lib/motionConfig';
import { AGENTS, BRAND } from '../../lib/brand';

/**
 * Command Bar — global ⌘K search + "Ask Agent" router.
 * Works from anywhere in the app. Zero-dependency on auth context
 * (it only shows destinations the user has reached via routes).
 *
 * Triggers: ⌘K, Ctrl+K, or `/` (outside of inputs)
 * Keys: Up/Down navigate, Enter opens, Esc closes, Tab filters type
 */

const SURFACE = {
  portal: [
    { type: 'nav',   icon: LayoutDashboard, label: 'Portal Dashboard',   to: '/portal/dashboard', scope: 'portal' },
    { type: 'nav',   icon: MessageSquare,   label: 'Agent Workspace',    to: '/portal/chat',      scope: 'portal' },
    { type: 'nav',   icon: FileText,        label: 'Knowledge Base',     to: '/portal/knowledge', scope: 'portal' },
    { type: 'nav',   icon: Wrench,          label: 'Tools',              to: '/portal/tools',     scope: 'portal' },
    { type: 'nav',   icon: BarChart3,       label: 'Analytics',          to: '/portal/analytics', scope: 'portal' },
    { type: 'nav',   icon: Key,             label: 'API Keys',           to: '/portal/api-keys',  scope: 'portal' },
    { type: 'nav',   icon: Receipt,         label: 'Billing',            to: '/portal/billing',   scope: 'portal' },
    { type: 'nav',   icon: Settings,        label: 'Settings',           to: '/portal/settings',  scope: 'portal' },
  ],
  admin: [
    { type: 'nav',   icon: LayoutDashboard, label: 'Admin Dashboard',    to: '/admin/dashboard',  scope: 'admin' },
    { type: 'nav',   icon: Users,           label: 'Tenants',            to: '/admin/tenants',    scope: 'admin' },
    { type: 'nav',   icon: Cpu,             label: 'GPT Config',         to: '/admin/gpt-config', scope: 'admin' },
    { type: 'nav',   icon: FileText,        label: 'Knowledge Base',     to: '/admin/knowledge',  scope: 'admin' },
    { type: 'nav',   icon: BarChart3,       label: 'Analytics',          to: '/admin/analytics',  scope: 'admin' },
    { type: 'nav',   icon: Hash,            label: 'Audit Logs',         to: '/admin/audit-logs', scope: 'admin' },
  ],
};

const RECENT_KEY = 'cognara.commandbar.recent';
const LEGACY_RECENT_KEY = 'capexara.commandbar.recent';
const LEGACY_RECENT_KEY_V2 = 'sutradhar.commandbar.recent';
const MAX_RECENT = 5;

export const CommandBar = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef(null);

  const scope = location.pathname.startsWith('/admin') ? 'admin' : 'portal';
  const dataset = SURFACE[scope] || [];

  // Global keybinding
  useEffect(() => {
    const onKey = (e) => {
      const isInput = ['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName);
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === '/' && !isInput && !open) {
        e.preventDefault();
        setOpen(true);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Focus input when opening
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 30);
      setQuery('');
      setCursor(0);
    }
  }, [open]);

  const recent = useMemo(() => {
    try {
      const raw = localStorage.getItem(RECENT_KEY) || localStorage.getItem(LEGACY_RECENT_KEY_V2) || localStorage.getItem(LEGACY_RECENT_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  }, [open]);

  const pushRecent = (item) => {
    try {
      const raw = localStorage.getItem(RECENT_KEY) || localStorage.getItem(LEGACY_RECENT_KEY) || '[]';
      const prev = JSON.parse(raw);
      const next = [{ ...item, at: Date.now() }, ...prev.filter((p) => p.to !== item.to)].slice(0, MAX_RECENT);
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    } catch {}
  };

  // Filtered list
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return dataset;
    return dataset.filter((x) => x.label.toLowerCase().includes(q));
  }, [query, dataset]);

  // Build result rows (always: "Ask Agent" row at top when query)
  const rows = useMemo(() => {
    const q = query.trim();
    const out = [];
    if (q) {
      out.push({
        kind: 'agent',
        icon: Sparkles,
        label: `Ask ${AGENTS.sutradhar.display}`,
        detail: q,
        to: `/portal/chat?q=${encodeURIComponent(q)}`,
      });
    } else if (recent.length > 0) {
      out.push({ kind: 'header', label: 'Recent' });
      recent.forEach((r) => out.push({ kind: 'recent', ...r }));
    }
    if (filtered.length > 0) {
      out.push({ kind: 'header', label: q ? 'Go to' : 'Quick navigate' });
      filtered.forEach((r) => out.push({ kind: 'nav', ...r }));
    }
    return out;
  }, [query, filtered, recent]);

  // Only navigable rows participate in cursor
  const navigableIndexes = rows.map((r, i) => (r.kind === 'header' ? -1 : i)).filter((i) => i >= 0);
  const safeCursor = Math.min(cursor, navigableIndexes.length - 1);
  const activeIdx = navigableIndexes[safeCursor];

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, navigableIndexes.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const row = rows[activeIdx];
      if (row) executeRow(row);
    }
  };

  const executeRow = (row) => {
    if (row.kind === 'agent') {
      pushRecent({ to: row.to, label: row.label, icon: 'Sparkles' });
      navigate(row.to);
    } else if (row.to) {
      pushRecent({ to: row.to, label: row.label });
      navigate(row.to);
    }
    setOpen(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 bg-black/60 backdrop-blur-[12px] z-[100]"
          />
          <div className="fixed inset-0 z-[101] flex items-start justify-center pt-[12vh] px-4 pointer-events-none">
            <motion.div
              {...modalCenter}
              role="dialog"
              aria-modal="true"
              aria-label="Command palette"
              className="w-full max-w-[640px] pointer-events-auto ig-floating overflow-hidden"
              style={{ background: 'var(--layer-3)', border: '1px solid var(--hairline-strong)' }}
              data-testid="command-bar"
            >
              {/* Input */}
              <div className="flex items-center gap-3 px-4 py-3.5 border-b border-[var(--hairline)]">
                <Search size={18} className="text-nexus-text-tertiary flex-shrink-0" />
                <input
                  ref={inputRef}
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); setCursor(0); }}
                  onKeyDown={onKeyDown}
                  placeholder={`Search ${BRAND.name}, or ask ${AGENTS.sutradhar.display}…`}
                  aria-label={`Search ${BRAND.name}`}
                  className="flex-1 bg-transparent outline-none text-[15px] text-nexus-text-primary placeholder:text-nexus-text-tertiary"
                  data-testid="command-bar-input"
                />
                <kbd className="ig-mono text-[10px] text-nexus-text-tertiary px-1.5 py-0.5 rounded border border-[var(--hairline)]">ESC</kbd>
              </div>

              {/* Results */}
              <div className="max-h-[52vh] overflow-y-auto py-2">
                {rows.length === 0 ? (
                  <div className="px-4 py-8 text-center text-[13px] text-nexus-text-tertiary">No matches. Try "dashboard", "chat", "tenants"…</div>
                ) : (
                  rows.map((row, i) => {
                    if (row.kind === 'header') {
                      return (
                        <div key={`h-${i}`} className="ig-eyebrow px-4 pt-3 pb-1.5 text-nexus-text-tertiary" style={{ color: 'var(--nexus-text-tertiary)' }}>
                          {row.label}
                        </div>
                      );
                    }
                    const Icon = row.icon || ArrowRight;
                    const active = i === activeIdx;
                    const isAgent = row.kind === 'agent';
                    return (
                      <button
                        key={`${row.kind}-${i}`}
                        onClick={() => executeRow(row)}
                        onMouseEnter={() => setCursor(navigableIndexes.indexOf(i))}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                          active ? 'bg-nexus-accent-mint/10' : 'hover:bg-white/[0.03]'
                        }`}
                        data-testid={`cb-row-${row.kind}`}
                      >
                        {isAgent ? (
                          <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{
                            background: 'linear-gradient(135deg, rgba(30,78,140,0.3), rgba(242,107,31,0.3))',
                            border: '1px solid rgba(30, 78, 140, 0.4)',
                          }}>
                            <Sparkles size={13} className="text-nexus-accent-mint" />
                          </div>
                        ) : (
                          <div className="w-7 h-7 rounded-lg bg-white/[0.04] border border-[var(--hairline)] flex items-center justify-center flex-shrink-0">
                            <Icon size={13} className="text-nexus-text-secondary" />
                          </div>
                        )}
                        <span className="flex-1 text-[13.5px] text-nexus-text-primary font-medium">{row.label}</span>
                        {isAgent && row.detail && (
                          <span className="ig-mono text-[11px] text-nexus-text-tertiary truncate max-w-[220px]">{row.detail}</span>
                        )}
                        {row.kind === 'recent' && row.at && (
                          <span className="ig-mono text-[10px] text-nexus-text-tertiary flex items-center gap-1">
                            <Clock size={10} /> {formatRel(row.at)}
                          </span>
                        )}
                        {row.scope && (
                          <span className="ig-eyebrow text-[9px]" style={{ color: 'var(--nexus-text-tertiary)' }}>{row.scope}</span>
                        )}
                        {active && <ArrowRight size={12} className="text-nexus-accent-mint" />}
                      </button>
                    );
                  })
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-t border-[var(--hairline)] bg-white/[0.02]">
                <div className="flex items-center gap-3 text-[10.5px] text-nexus-text-tertiary">
                  <span className="flex items-center gap-1"><kbd className="ig-mono px-1 py-0.5 rounded border border-[var(--hairline)]">↑↓</kbd> navigate</span>
                  <span className="flex items-center gap-1"><kbd className="ig-mono px-1 py-0.5 rounded border border-[var(--hairline)]">↵</kbd> open</span>
                  <span className="flex items-center gap-1"><kbd className="ig-mono px-1 py-0.5 rounded border border-[var(--hairline)]">/</kbd> anywhere</span>
                </div>
                <span className="ig-eyebrow text-[9px] flex items-center gap-1" style={{ color: 'var(--nexus-accent-blue)' }}>
                  <Zap size={9} /> {BRAND.name}
                </span>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

function formatRel(ts) {
  const diff = (Date.now() - ts) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

export default CommandBar;
